/**
 * Why is this shift still owed? — the Chase row chip (Ed, 2026-09-22).
 *
 * The label is computed hourly by the chase reconciler (app.dsr
 * join.diagnose) and stored on the case; this module only words it. Pure,
 * no imports, Node-testable.
 *
 * Tone = how sure we are it's a carer's missed record: red = probably
 * missed; blue = check it against the spreadsheet; amber = a split shift
 * (real under the two-records rule, but the carer did write something);
 * grey = not the carer's to fix.
 */

export type Tone = "red" | "blue" | "amber" | "grey";

export interface DiagnosisChip {
  label: string;
  tip: string;
  tone: Tone;
}

const CHIPS: Record<string, DiagnosisChip> = {
  missing: {
    label: "No MachForm found yet",
    tip: "No MachForm has been found for this shift yet. The client has MachForms on nearby days, so this is most likely a missed form.",
    tone: "red",
  },
  "quiet-week": {
    label: "No records all week",
    tip: "The client has no records at all within a week either side — a missed run of records, or the client's form has gone quiet.",
    tone: "red",
  },
  "same-day-unmatched": {
    label: "Record that day — check",
    tip: "A record exists for this day, but its times didn't match this shift. Check it against the spreadsheet: it may be one record written for two shifts, or times typed wrongly.",
    tone: "blue",
  },
  "split-slot": {
    label: "Split shift — one record?",
    tip: "This slot runs straight on from another slot for the same client and carer that has a record. Each slot needs its own record.",
    tone: "amber",
  },
  "stranded-recordings": {
    label: "Recorded on a moved visit",
    tip: "The carer did write a record that day, but on an earlier version of the visit that OneTouch has since moved, shortened or reassigned. The record exists; it just isn't attached to this shift. Check it in the client's log for the day and use \"Resolve manually\" if it covers the shift.",
    tone: "amber",
  },
  "no-form": {
    label: "No form for client",
    tip: "This client has no shift-record form we recognise in MachForms, so nothing can clear this row. An office fix: create or rename the form, or tell Ed.",
    tone: "grey",
  },
};

/** The chip for a stored diagnosis, or null when none should show.
 *  rota-gap gets no chip: the row already says "unassigned". */
export function diagnosisChip(diagnosis: string | null | undefined): DiagnosisChip | null {
  if (!diagnosis || diagnosis === "rota-gap") return null;
  return CHIPS[diagnosis] ?? null;
}
